import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, IconButton, CircularProgress,
  Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Divider, Alert, Fade, Badge, InputAdornment, List, ListItem,
  ListItemText, ListItemButton, Menu, MenuItem, Snackbar,
} from "@mui/material";
import SendIcon            from "@mui/icons-material/Send";
import CloseIcon           from "@mui/icons-material/Close";
import SmartToyIcon        from "@mui/icons-material/SmartToy";
import EmailIcon           from "@mui/icons-material/Email";
import ContentCopyIcon     from "@mui/icons-material/ContentCopy";
import HistoryIcon         from "@mui/icons-material/History";
import DeleteOutlineIcon   from "@mui/icons-material/DeleteOutline";
import BookmarkIcon        from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon  from "@mui/icons-material/BookmarkBorder";
import HelpOutlineIcon     from "@mui/icons-material/HelpOutline";
import PictureAsPdfIcon    from "@mui/icons-material/PictureAsPdf";
import MoreVertIcon        from "@mui/icons-material/MoreVert";
import MicIcon             from "@mui/icons-material/Mic";
import ExpandMoreIcon      from "@mui/icons-material/ExpandMore";
import jsPDF               from "jspdf";
import api from "../../utils/api";

/* ── Markdown table renderer ── */
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("### ")) { elements.push(<Typography key={i} variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mt:1.5, mb:0.25 }}>{line.slice(4)}</Typography>); i++; continue; }
    if (line.startsWith("## "))  { elements.push(<Typography key={i} variant="subtitle1" sx={{ fontWeight:700, color:"#223142", mt:1.5, mb:0.25 }}>{line.slice(3)}</Typography>); i++; continue; }
    if (line.startsWith("# "))   { elements.push(<Typography key={i} variant="h6" sx={{ fontWeight:800, color:"#223142", mt:1.5, mb:0.25 }}>{line.slice(2)}</Typography>); i++; continue; }
    if (line.startsWith("---"))  { elements.push(<Divider key={i} sx={{ my:1 }} />); i++; continue; }
    if (line.trim().startsWith("|") && lines[i+1]?.includes("---")) {
      const headers = line.split("|").filter(s => s.trim()).map(s => s.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) { rows.push(lines[i].split("|").filter(s=>s.trim()).map(s=>s.trim())); i++; }
      elements.push(
        <Box key={`t-${i}`} sx={{ overflowX:"auto", mb:1.5, borderRadius:"8px", border:"1px solid rgba(47,99,173,0.12)" }}>
          <table style={{ borderCollapse:"collapse", width:"100%", fontSize:"0.75rem" }}>
            <thead><tr style={{ backgroundColor:"rgba(47,99,173,0.06)" }}>{headers.map((h,j)=><th key={j} style={{ padding:"5px 10px", textAlign:"left", fontWeight:700, color:"#223142", borderBottom:"1px solid rgba(47,99,173,0.12)", whiteSpace:"nowrap" }}>{h}</th>)}</tr></thead>
            <tbody>{rows.map((row,ri)=><tr key={ri} style={{ backgroundColor:ri%2===0?"#fff":"rgba(47,99,173,0.02)" }}>{row.map((cell,ci)=><td key={ci} style={{ padding:"4px 10px", color:"#374151", borderBottom:"1px solid rgba(47,99,173,0.05)" }}>{cell}</td>)}</tr>)}</tbody>
          </table>
        </Box>
      );
      continue;
    }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      elements.push(<Typography key={i} component="div" variant="body2" sx={{ color:"#374151", pl:1.5, lineHeight:1.7, "&:before":{ content:"'•'", mr:0.75, color:"#2f63ad" } }}>{line.slice(2)}</Typography>);
      i++; continue;
    }
    if (line.trim()) {
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      elements.push(<Typography key={i} variant="body2" sx={{ color:"#374151", lineHeight:1.7, mb:0.2 }}>{parts.map((p,j)=>p.startsWith("**")?<strong key={j} style={{color:"#223142"}}>{p.slice(2,-2)}</strong>:p)}</Typography>);
    } else { elements.push(<Box key={i} sx={{ height:"0.4rem" }} />); }
    i++;
  }
  return elements;
}

/* ── Extract suggested follow-ups from response ── */
function extractFollowUps(text) {
  if (!text) return [];
  const match = text.match(/\*\*Suggested follow-ups:\*\*([\s\S]*?)(?=\n\n|\n##|$)/i);
  if (!match) return [];
  return match[1].trim().split("\n")
    .map(l => l.replace(/^[-*]\s*/, "").trim())
    .filter(Boolean)
    .slice(0, 3);
}

function removeFollowUps(text) {
  return text?.replace(/---\s*\n\*\*Suggested follow-ups:\*\*[\s\S]*?(?=\n\n\n|$)/i, "").trim() || text;
}

/* ── PDF export ── */
function exportToPDF(messages) {
  const doc = new jsPDF();
  doc.setFontSize(16);
  doc.setTextColor(47,99,173);
  doc.text("HOA AI Assistant. Conversation Export", 15, 20);
  doc.setFontSize(9);
  doc.setTextColor(100,100,100);
  doc.text(new Date().toLocaleString(), 15, 27);
  doc.setDrawColor(47,99,173);
  doc.line(15, 30, 195, 30);
  let y = 38;
  doc.setFontSize(10);
  messages.forEach(msg => {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.setTextColor(msg.role==="user" ? 21:34, msg.role==="user"?76:49, msg.role==="user"?193:74);
    doc.setFont(undefined, "bold");
    doc.text(msg.role === "user" ? "You:" : "HOA Assistant:", 15, y);
    y += 5;
    doc.setFont(undefined, "normal");
    doc.setTextColor(60,60,60);
    const plain = (msg.content||"").replace(/[*#|]/g," ").replace(/\s+/g," ").trim();
    const lines = doc.splitTextToSize(plain, 175);
    lines.slice(0,30).forEach(l => { if(y>270){doc.addPage();y=20;} doc.text(l,15,y); y+=4.5; });
    y += 4;
  });
  doc.save(`hoa-chat-${Date.now()}.pdf`);
}

/* ── Message bubble ── */
function MessageBubble({ msg, onEmail, onCopy, onSaveQuery, onFollowUp, showSuggestions }) {
  const isUser = msg.role === "user";
  const followUps = isUser ? [] : extractFollowUps(msg.content||"");
  const cleanContent = isUser ? msg.content : removeFollowUps(msg.content||"");
  return (
    <Box sx={{ display:"flex", flexDirection:"column", alignItems:isUser?"flex-end":"flex-start", mb:1.5 }}>
      <Typography variant="caption" sx={{ color:"#9aa5b4", mb:0.3, mx:1, fontSize:"0.6rem", fontWeight:600 }}>{isUser?"You":"HOA Assistant"}</Typography>
      <Box sx={{
        maxWidth:"92%", px:1.75, py:1.25,
        borderRadius:isUser?"16px 16px 4px 16px":"4px 16px 16px 16px",
        backgroundColor:isUser?"#2f63ad":"#f8fafc",
        border:isUser?"none":"1px solid rgba(47,99,173,0.10)",
        boxShadow:"0 1px 4px rgba(0,0,0,0.06)",
      }}>
        {isUser
          ? <Typography variant="body2" sx={{ color:"#fff", lineHeight:1.6 }}>{msg.content}</Typography>
          : <Box>{renderMarkdown(cleanContent)}</Box>
        }
      </Box>
      {!isUser && (
        <Box sx={{ display:"flex", gap:0.75, mt:0.5, mx:1, flexWrap:"wrap", alignItems:"center" }}>
          {msg.tools_used?.map(t=><Chip key={t} label={t.replace("query_","")} size="small" sx={{ height:15, fontSize:"0.57rem", backgroundColor:"rgba(47,99,173,0.08)", color:"#2f63ad" }} />)}
          {msg.tokens_used && <Chip label={`${msg.tokens_used.total} tokens`} size="small" sx={{ height:15, fontSize:"0.57rem", backgroundColor:"rgba(107,119,140,0.08)", color:"#6b778c" }} />}
          <Tooltip title="Copy"><IconButton size="small" sx={{ p:0.2 }} onClick={()=>onCopy(msg.content)}><ContentCopyIcon sx={{ fontSize:11, color:"#9aa5b4" }} /></IconButton></Tooltip>
          <Tooltip title="Email response"><IconButton size="small" sx={{ p:0.2 }} onClick={()=>onEmail(msg)}><EmailIcon sx={{ fontSize:11, color:"#9aa5b4" }} /></IconButton></Tooltip>
          <Tooltip title="Save this question"><IconButton size="small" sx={{ p:0.2 }} onClick={()=>onSaveQuery(msg)}><BookmarkBorderIcon sx={{ fontSize:11, color:"#9aa5b4" }} /></IconButton></Tooltip>
        </Box>
      )}
      {/* Suggested follow-ups */}
      {!isUser && showSuggestions && followUps.length > 0 && (
        <Box sx={{ mx:1, mt:0.5, display:"flex", flexWrap:"wrap", gap:0.5 }}>
          {followUps.map((q,i) => (
            <Chip key={i} label={q} size="small" onClick={()=>onFollowUp(q)} sx={{ cursor:"pointer", backgroundColor:"rgba(47,99,173,0.06)", color:"#2f63ad", fontSize:"0.65rem", "&:hover":{ backgroundColor:"rgba(47,99,173,0.14)" } }} />
          ))}
        </Box>
      )}
    </Box>
  );
}

/* ══════════════════════════════════════════════════════
   HOW TO USE DIALOG
══════════════════════════════════════════════════════ */
function HowToUseDialog({ open, onClose, activeModules = [] }) {
  const hasModule = (mod) => activeModules.length === 0 || activeModules.some(m => m.includes(mod));
  const allExamples = [
    { category:"Members",              module:null,           qs:["Show full profile for member 1010","Find member named Brady Dick","Who has the most violations?"] },
    { category:"Work Orders",          module:"work",         qs:["Open work orders for member 1010","Work orders this month","Which work orders are overdue 30+ days?"] },
    { category:"Violations",           module:"violation",    qs:["Violations for member 1010","Escalating violations (2nd notice or higher)","Violation trend this year by month"] },
    { category:"Architectural",        module:"architectural",qs:["Architectural requests this month","Pending AR requests","AR requests for member 1010"] },
    { category:"Marina",               module:"marina",       qs:["Show marina occupancy","Vessels with expired insurance","Reservations arriving today"] },
    { category:"Billing",              module:null,           qs:["Pending billing records","Billing for member 1010","Total pending amount this month"] },
    { category:"Reports & Triage",     module:null,           qs:["Give me today's full triage. what needs attention?","Top 10 members with most open violations","Monthly work order volume for the last 6 months"] },
  ];
  const examples = allExamples.filter(e => !e.module || hasModule(e.module));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:"18px" } }}>
      <DialogTitle sx={{ fontWeight:800, color:"#223142", display:"flex", alignItems:"center", gap:1 }}>
        <SmartToyIcon sx={{ color:"#2f63ad" }} /> HOA AI Assistant. How to Use
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb:2, borderRadius:"10px", fontSize:"0.78rem" }}>
          Ask questions in plain English. The AI queries live HOA data and returns formatted tables. It understands dates like "this month", "last 30 days", and member names.
        </Alert>
        {examples.map(cat => (
          <Box key={cat.category} sx={{ mb:2 }}>
            <Typography variant="caption" sx={{ fontWeight:700, color:"#6b778c", textTransform:"uppercase", letterSpacing:0.8 }}>{cat.category}</Typography>
            <Box sx={{ display:"flex", flexWrap:"wrap", gap:0.5, mt:0.5 }}>
              {cat.qs.map(q => <Chip key={q} label={q} size="small" sx={{ fontSize:"0.68rem", backgroundColor:"rgba(47,99,173,0.06)", color:"#2f63ad" }} />)}
            </Box>
          </Box>
        ))}
        <Divider sx={{ my:2 }} />
        <Typography variant="caption" sx={{ color:"#6b778c", fontWeight:600, display:"block", mb:1 }}>TIPS</Typography>
        {["Responses always include suggested follow-up questions. click them to continue","Save frequently used queries with the bookmark icon","Export any conversation to PDF with the menu button","Email responses directly from the chat","All queries are logged for audit purposes"].map((t,i) => (
          <Typography key={i} variant="caption" sx={{ display:"block", color:"#5f6b7a", lineHeight:1.8 }}>• {t}</Typography>
        ))}
        <Divider sx={{ my:2 }} />
        <Typography variant="caption" sx={{ color:"#9aa5b4" }}> AI · For HOA staff use only · Max {localStorage.getItem("aiMaxTokens")||5000} tokens per response</Typography>
      </DialogContent>
      <DialogActions sx={{ px:3, pb:2.5 }}>
        <Button onClick={onClose} variant="contained" sx={{ background:"#2f63ad", borderRadius:"10px", textTransform:"none", fontWeight:700 }}>Got it</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ════════════════════════════════════════════════════════
   MAIN WIDGET
════════════════════════════════════════════════════════ */
const AIChatWidget = () => {
  // Check for token immediately - never render on login/public pages
  const hasToken = !!(localStorage.getItem("authToken") || sessionStorage.getItem("authToken"));

  const [open,          setOpen]          = useState(false);
  const [access,        setAccess]        = useState(hasToken ? null : { allowed: false });
  const [messages,      setMessages]      = useState([]);
  const [input,         setInput]         = useState("");
  const [loading,       setLoading]       = useState(false);
  const [emailDialog,   setEmailDialog]   = useState(null);
  const [emailAddr,     setEmailAddr]     = useState(()=>localStorage.getItem("userEmail")||"");
  const [emailSending,  setEmailSending]  = useState(false);
  const [logOpen,       setLogOpen]       = useState(false);
  const [logs,          setLogs]          = useState([]);
  const [logsLoading,   setLogsLoading]   = useState(false);
  const [helpOpen,      setHelpOpen]      = useState(false);
  const [savedOpen,     setSavedOpen]     = useState(false);
  const [savedQueries,  setSavedQueries]  = useState([]);
  const [saveDialog,    setSaveDialog]    = useState(null); // { question }
  const [saveName,      setSaveName]      = useState("");
  const [snack,         setSnack]         = useState("");
  const [tokenTotal,    setTokenTotal]    = useState(0);
  const [menuAnchor,    setMenuAnchor]    = useState(null);
  const [showFollowUps, setShowFollowUps] = useState(true);
  const [activeModules, setActiveModules] = useState([]); // module names from server
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!hasToken) return; // no token = definitely not logged in, skip API call
    api.get("/ai-chat/access").then(r => {
      setAccess(r.data);
      if (r.data.max_tokens) localStorage.setItem("aiMaxTokens", r.data.max_tokens);
      if (r.data.active_modules) setActiveModules(r.data.active_modules.map(m => m.toLowerCase()));
    }).catch(() => setAccess({ allowed: false }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [messages, open]);

  const send = useCallback(async (q) => {
    const question = (q || input).trim();
    if (!question || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role:"user", content:question }]);
    setLoading(true);
    try {
      const history = messages.slice(-10).map(m=>({role:m.role, content:m.content}));
      const res = await api.post("/ai-chat", { question, history });
      const d = res.data;
      setMessages(prev => [...prev, { role:"assistant", content:d.response, tools_used:d.tools_used, tokens_used:d.tokens_used }]);
      setTokenTotal(t => t + (d.tokens_used?.total||0));
    } catch (e) {
      const msg = e.response?.data?.message || "Request failed.";
      setMessages(prev => [...prev, { role:"assistant", content:`**Error:** ${msg}` }]);
      if (e.response?.status === 429) setSnack(msg);
    } finally { setLoading(false); }
  }, [input, loading, messages]);

  const handleKey = (e) => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  const openEmailDialog = (msg) => {
    const idx = messages.indexOf(msg);
    const question = messages.slice(0,idx).reverse().find(m=>m.role==="user")?.content||"";
    setEmailDialog({ content:msg.content, question });
  };

  const sendEmail = async () => {
    if (!emailAddr || !emailDialog) return;
    setEmailSending(true);
    try {
      await api.post("/ai-chat", { question:emailDialog.question, history:[], send_email_to:emailAddr });
      setSnack("Email sent!"); setEmailDialog(null);
    } catch { setSnack("Failed to send email."); }
    finally { setEmailSending(false); }
  };

  const copyToClipboard = (text) => { navigator.clipboard?.writeText(text); setSnack("Copied!"); setTimeout(()=>setSnack(""),2000); };

  const loadLogs = async () => { setLogsLoading(true); try { const r = await api.get("/ai-chat/logs?limit=50"); setLogs(r.data); } catch {} finally { setLogsLoading(false); } };

  const loadSaved = async () => { try { const r = await api.get("/ai-chat/saved-queries"); setSavedQueries(r.data||[]); } catch {} };

  const openSaveDialog = (msg) => {
    const idx = messages.indexOf(msg);
    const question = messages.slice(0,idx).reverse().find(m=>m.role==="user")?.content||"";
    setSaveDialog({ question }); setSaveName("");
  };

  const saveQuery = async () => {
    if (!saveName.trim() || !saveDialog) return;
    try {
      await api.post("/ai-chat/saved-queries", { name:saveName.trim(), question:saveDialog.question, is_global:false });
      setSnack("Query saved!"); setSaveDialog(null); loadSaved();
    } catch { setSnack("Failed to save."); }
  };

  const runSaved = async (sq) => {
    setSavedOpen(false);
    api.post(`/ai-chat/saved-queries/${sq.id}/run`).catch(()=>{});
    send(sq.question);
  };

  const clearChat = () => { setMessages([]); setTokenTotal(0); };

  if (!access?.allowed) return null;

  // Suggestions filtered to only active modules
  const hasModule = (mod) => activeModules.length === 0 || activeModules.some(m => m.includes(mod));
  const ALL_SUGGESTIONS = [
    { text:"Show work orders for member 1010",   module:"work" },
    { text:"What needs attention today?",         module:null },
    { text:"Who has the most violations?",        module:"violation" },
    { text:"Show marina occupancy",               module:"marina" },
    { text:"Pending billing records",             module:null },
    { text:"Violation trend this year",           module:"violation" },
    { text:"Vessels with expired insurance",      module:"marina" },
    { text:"Architectural requests this month",   module:"architectural" },
  ];
  const SUGGESTIONS = ALL_SUGGESTIONS.filter(s => !s.module || hasModule(s.module)).map(s => s.text);

  return (
    <>
      {/* Floating button */}
      <Fade in>
        <Box sx={{ position:"fixed", bottom:24, right:24, zIndex:1400 }}>
          {snack && <Fade in><Box sx={{ position:"absolute", bottom:64, right:0, background:"#223142", color:"#fff", px:2, py:1, borderRadius:"10px", fontSize:"0.75rem", whiteSpace:"nowrap", maxWidth:260 }}>{snack}</Box></Fade>}
          <Tooltip title={open?"Close":"HOA AI Assistant"} placement="left">
            <Badge badgeContent={messages.filter(m=>m.role==="user").length||null} color="primary" overlap="circular">
              <IconButton onClick={()=>setOpen(o=>!o)} sx={{ width:54, height:54, backgroundColor:"#2f63ad", color:"#fff", boxShadow:"0 4px 20px rgba(47,99,173,0.45)", "&:hover":{ backgroundColor:"#1e4c8a" } }}>
                {open?<CloseIcon/>:<SmartToyIcon/>}
              </IconButton>
            </Badge>
          </Tooltip>
        </Box>
      </Fade>

      {/* Chat window */}
      <Fade in={open} unmountOnExit>
        <Paper elevation={0} sx={{ position:"fixed", bottom:90, right:24, zIndex:1399, width:{ xs:"calc(100vw - 32px)", sm:540 }, height:620, display:"flex", flexDirection:"column", borderRadius:"20px", border:"1px solid rgba(47,99,173,0.15)", boxShadow:"0 20px 60px rgba(0,0,0,0.18)", overflow:"hidden" }}>

          {/* Header */}
          <Box sx={{ px:2.5, py:1.5, background:"linear-gradient(135deg,#2f63ad 0%,#1e4c8a 100%)", display:"flex", alignItems:"center", gap:1.25 }}>
            <SmartToyIcon sx={{ color:"#fff", fontSize:20 }} />
            <Box sx={{ flex:1 }}>
              <Typography variant="subtitle2" sx={{ color:"#fff", fontWeight:700, lineHeight:1.2 }}>HOA Assistant</Typography>
              <Typography variant="caption" sx={{ color:"rgba(255,255,255,0.65)", fontSize:"0.6rem" }}>
                {tokenTotal>0 ? `${tokenTotal.toLocaleString()} tokens used this session` : "Internal AI · HOA data only"}
              </Typography>
            </Box>
            <Tooltip title="How to use"><IconButton size="small" onClick={()=>setHelpOpen(true)} sx={{ color:"rgba(255,255,255,0.7)","&:hover":{color:"#fff"} }}><HelpOutlineIcon sx={{ fontSize:17 }}/></IconButton></Tooltip>
            <Tooltip title="Saved queries"><IconButton size="small" onClick={()=>{setSavedOpen(true);loadSaved();}} sx={{ color:"rgba(255,255,255,0.7)","&:hover":{color:"#fff"} }}><BookmarkIcon sx={{ fontSize:17 }}/></IconButton></Tooltip>
            <Tooltip title="Audit log"><IconButton size="small" onClick={()=>{setLogOpen(true);loadLogs();}} sx={{ color:"rgba(255,255,255,0.7)","&:hover":{color:"#fff"} }}><HistoryIcon sx={{ fontSize:17 }}/></IconButton></Tooltip>
            <IconButton size="small" onClick={e=>setMenuAnchor(e.currentTarget)} sx={{ color:"rgba(255,255,255,0.7)","&:hover":{color:"#fff"} }}><MoreVertIcon sx={{ fontSize:17 }}/></IconButton>
            <IconButton size="small" onClick={()=>setOpen(false)} sx={{ color:"rgba(255,255,255,0.7)","&:hover":{color:"#fff"} }}><CloseIcon sx={{ fontSize:17 }}/></IconButton>
          </Box>

          {/* Menu */}
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={()=>setMenuAnchor(null)}>
            <MenuItem onClick={()=>{clearChat();setMenuAnchor(null);}}><DeleteOutlineIcon sx={{ mr:1, fontSize:17 }}/>Clear chat</MenuItem>
            <MenuItem onClick={()=>{exportToPDF(messages);setMenuAnchor(null);}} disabled={!messages.length}><PictureAsPdfIcon sx={{ mr:1, fontSize:17 }}/>Export PDF</MenuItem>
            <MenuItem onClick={()=>{setShowFollowUps(s=>!s);setMenuAnchor(null);}}>{showFollowUps?"Hide":"Show"} follow-up suggestions</MenuItem>
          </Menu>

          {/* Messages */}
          <Box sx={{ flex:1, overflowY:"auto", px:2, py:1.5, backgroundColor:"#fafbff", "&::-webkit-scrollbar":{width:3},"&::-webkit-scrollbar-thumb":{backgroundColor:"rgba(47,99,173,0.15)",borderRadius:2} }}>
            {messages.length === 0 && (
              <Box sx={{ py:3, textAlign:"center" }}>
                <SmartToyIcon sx={{ fontSize:36, color:"rgba(47,99,173,0.18)", mb:0.75, display:"block", mx:"auto" }} />
                <Typography variant="body2" sx={{ color:"#9aa5b4", fontWeight:600, mb:0.5 }}>HOA AI Assistant</Typography>
                <Typography variant="caption" sx={{ color:"#b8c4d0", display:"block", mb:2.5 }}>Ask me anything about HOA operations</Typography>
                <Box sx={{ display:"flex", flexWrap:"wrap", gap:0.5, justifyContent:"center" }}>
                  {SUGGESTIONS.map(s=><Chip key={s} label={s} size="small" onClick={()=>send(s)} sx={{ cursor:"pointer", backgroundColor:"rgba(47,99,173,0.06)", color:"#2f63ad", fontSize:"0.67rem","&:hover":{backgroundColor:"rgba(47,99,173,0.12)"} }} />)}
                </Box>
                <Button size="small" startIcon={<HelpOutlineIcon sx={{ fontSize:14 }} />} onClick={()=>setHelpOpen(true)} sx={{ mt:2, color:"#6b778c", textTransform:"none", fontSize:"0.72rem" }}>How to use</Button>
              </Box>
            )}
            {messages.map((msg,i) => (
              <MessageBubble key={i} msg={msg} onEmail={openEmailDialog} onCopy={copyToClipboard}
                onSaveQuery={openSaveDialog} onFollowUp={q=>send(q)} showSuggestions={showFollowUps && i===messages.length-1} />
            ))}
            {loading && (
              <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:1.5 }}>
                <CircularProgress size={14} sx={{ color:"#2f63ad" }} />
                <Typography variant="caption" sx={{ color:"#9aa5b4", fontStyle:"italic" }}>Querying HOA data…</Typography>
              </Box>
            )}
            <div ref={bottomRef} />
          </Box>

          {/* Input */}
          <Divider />
          <Box sx={{ px:1.5, py:1.25, backgroundColor:"#fff" }}>
            <TextField fullWidth multiline maxRows={3} size="small"
              placeholder="Ask about members, work orders, violations, marina, billing…"
              value={input} onChange={e=>setInput(e.target.value)} onKeyDown={handleKey} disabled={loading}
              InputProps={{ sx:{ borderRadius:"12px", fontSize:"0.8rem", backgroundColor:"#f8faff" },
                endAdornment: <InputAdornment position="end">
                  <IconButton size="small" onClick={()=>send()} disabled={!input.trim()||loading} sx={{ color:input.trim()?"#2f63ad":"#c8d0dc" }}>
                    {loading?<CircularProgress size={16}/>:<SendIcon sx={{ fontSize:17 }}/>}
                  </IconButton>
                </InputAdornment>
              }} />
            <Typography variant="caption" sx={{ color:"#d0d7e0", fontSize:"0.58rem", mt:0.4, display:"block", textAlign:"right" }}>
              Internal use only · Press Enter to send
            </Typography>
          </Box>
        </Paper>
      </Fade>

      {/* How to Use */}
      <HowToUseDialog open={helpOpen} onClose={()=>setHelpOpen(false)} activeModules={activeModules} />

      {/* Saved Queries */}
      <Dialog open={savedOpen} onClose={()=>setSavedOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx:{borderRadius:"16px"} }}>
        <DialogTitle sx={{ fontWeight:700, display:"flex", alignItems:"center", gap:1 }}><BookmarkIcon sx={{ color:"#2f63ad" }}/>Saved Queries</DialogTitle>
        <DialogContent sx={{ p:0 }}>
          {savedQueries.length === 0
            ? <Box sx={{ py:4, textAlign:"center" }}><Typography variant="body2" sx={{ color:"#9aa5b4" }}>No saved queries yet. Bookmark a question using the 🔖 icon.</Typography></Box>
            : <List dense>
                {savedQueries.map(sq => (
                  <ListItem key={sq.id} disablePadding secondaryAction={
                    <IconButton size="small" onClick={async()=>{await api.delete(`/ai-chat/saved-queries/${sq.id}`);loadSaved();}}><CloseIcon sx={{ fontSize:14 }}/></IconButton>
                  }>
                    <ListItemButton onClick={()=>runSaved(sq)} sx={{ borderRadius:"8px" }}>
                      <ListItemText primary={sq.name} secondary={sq.question.substring(0,80)+(sq.question.length>80?"…":"")}
                        primaryTypographyProps={{ fontWeight:700, fontSize:"0.82rem" }}
                        secondaryTypographyProps={{ fontSize:"0.72rem", color:"#6b778c" }} />
                      {sq.run_count > 0 && <Chip label={`${sq.run_count}×`} size="small" sx={{ height:16, fontSize:"0.6rem", ml:1 }} />}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
          }
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2 }}><Button onClick={()=>setSavedOpen(false)} sx={{ textTransform:"none",color:"#6b778c" }}>Close</Button></DialogActions>
      </Dialog>

      {/* Save Query Dialog */}
      <Dialog open={!!saveDialog} onClose={()=>setSaveDialog(null)} maxWidth="xs" fullWidth PaperProps={{ sx:{borderRadius:"16px"} }}>
        <DialogTitle sx={{ fontWeight:700, pb:0.5 }}>Save Query</DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ color:"#6b778c", mb:1.5, display:"block" }}>"{saveDialog?.question?.substring(0,80)}"</Typography>
          <TextField fullWidth size="small" label="Name this query" value={saveName} onChange={e=>setSaveName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveQuery()} autoFocus sx={{ "& .MuiOutlinedInput-root":{borderRadius:"10px"} }} />
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2.5 }}>
          <Button onClick={()=>setSaveDialog(null)} sx={{ textTransform:"none",color:"#6b778c" }}>Cancel</Button>
          <Button variant="contained" onClick={saveQuery} disabled={!saveName.trim()} sx={{ background:"#2f63ad","&:hover":{background:"#1e4c8a"},borderRadius:"10px",textTransform:"none",fontWeight:700 }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={!!emailDialog} onClose={()=>setEmailDialog(null)} maxWidth="xs" fullWidth PaperProps={{ sx:{borderRadius:"16px"} }}>
        <DialogTitle sx={{ fontWeight:700, pb:0.5 }}>Send by Email</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="Email address" type="email" value={emailAddr} onChange={e=>setEmailAddr(e.target.value)} autoFocus sx={{ "& .MuiOutlinedInput-root":{borderRadius:"10px"} }} />
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2.5 }}>
          <Button onClick={()=>setEmailDialog(null)} sx={{ textTransform:"none",color:"#6b778c" }}>Cancel</Button>
          <Button variant="contained" disabled={!emailAddr||emailSending} onClick={sendEmail} startIcon={emailSending?<CircularProgress size={13} color="inherit"/>:<EmailIcon/>} sx={{ background:"#2f63ad","&:hover":{background:"#1e4c8a"},borderRadius:"10px",textTransform:"none",fontWeight:700 }}>
            {emailSending?"Sending…":"Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Log */}
      <Dialog open={logOpen} onClose={()=>setLogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx:{borderRadius:"16px"} }}>
        <DialogTitle sx={{ fontWeight:700, display:"flex", alignItems:"center", gap:1 }}><HistoryIcon sx={{ color:"#2f63ad" }}/>Chat Audit Log</DialogTitle>
        <DialogContent sx={{ p:0 }}>
          {logsLoading ? <Box sx={{ py:4,textAlign:"center" }}><CircularProgress size={22}/></Box>
          : logs.length===0 ? <Box sx={{ py:4,textAlign:"center" }}><Typography variant="body2" sx={{ color:"#9aa5b4" }}>No activity yet</Typography></Box>
          : <Box sx={{ maxHeight:480, overflowY:"auto" }}>
              {logs.map(log=>(
                <Box key={log.id} sx={{ px:3, py:1.75, borderBottom:"1px solid rgba(47,99,173,0.06)","&:hover":{backgroundColor:"rgba(47,99,173,0.02)"} }}>
                  <Box sx={{ display:"flex", justifyContent:"space-between", mb:0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight:700, color:"#223142" }}>{log.username}</Typography>
                    <Typography variant="caption" sx={{ color:"#9aa5b4" }}>{new Date(log.created_at).toLocaleString()}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color:"#2f63ad", fontStyle:"italic", mb:0.5, fontSize:"0.75rem" }}>"{log.question?.substring(0,90)}{log.question?.length>90?"…":""}"</Typography>
                  <Box sx={{ display:"flex", gap:0.75, flexWrap:"wrap" }}>
                    {log.tokens_in&&<Chip label={`${((log.tokens_in||0)+(log.tokens_out||0)).toLocaleString()} tokens`} size="small" sx={{ height:16, fontSize:"0.58rem" }}/>}
                    {log.error&&<Chip label="Error" size="small" sx={{ height:16, fontSize:"0.58rem", backgroundColor:"rgba(220,38,38,0.1)",color:"#dc2626" }}/>}
                    {log.emailed_to&&<Chip label={`→ ${log.emailed_to}`} size="small" sx={{ height:16, fontSize:"0.58rem", backgroundColor:"rgba(22,163,74,0.1)",color:"#15803d" }}/>}
                  </Box>
                </Box>
              ))}
            </Box>
          }
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2 }}><Button onClick={()=>setLogOpen(false)} sx={{ textTransform:"none",color:"#6b778c" }}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={()=>setSnack("")} anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity="info" onClose={()=>setSnack("")} sx={{ borderRadius:"10px" }}>{snack}</Alert>
      </Snackbar>
    </>
  );
};

export default AIChatWidget;
